import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Book} from "../../../../../model/Book";
import {BookService} from "../../../../service-data/book.service";
import {CoverService} from "../../../../service-data/cover.service";
import {BodyService} from "../../../../service-data/body.service";
import {ResourceService} from "../../../../service-data/resource.service";
import {Observable} from "rxjs";
import {bestAvailableStage, PdfArtifactStatus} from "../../../../service-ui/pdf-artifact-stage";
import {requestStagedImage, StageWatch} from "../../../../service-ui/staged-preview-image";
import {paintCoverWhitePlate} from "../../../../service-ui/cover-white-plate";

/**
 * The Book cover thumbnail used across the projects surfaces.
 *
 * ── What changed on 2026-09-15 ───────────────────────────────────────────
 * This component carried the pre-fix copy of the preview code — the same one
 * that caused blank Draft thumbnails until it was corrected in
 * `ProjectDraftCoverImageComponent`. A copy does not get a fix, so it still
 * had all three defects:
 *
 *   1. **Self-poisoning cache.** A src-less `<img>` was appended under the
 *      real id *before* the URL arrived (and then appended a second time
 *      inside the callback, under a slightly different id). Every later
 *      attempt found the empty one, read it as a hit — `complete` is `true`
 *      for a src-less image — and copied its empty `src` onto the visible
 *      element. A thumbnail that missed once stayed blank until a reload.
 *   2. **No retry trigger.** `ngOnChanges` was the only entry point, and it
 *      cannot fire when `addUpdateLocalItem` mutates the cached Book in place.
 *   3. **Two ids for one image.** The outer element used `imageId` while the
 *      callback set `'cropped-cover-' + resourceId` — identical only for the
 *      perfect-bound case, and divergent for SADDLE_STITCH.
 *
 * All three are gone: `requestStagedImage` owns the request path, and the
 * component subscribes to the three notifiers that can change what should be
 * on screen.
 */
@Component({
  selector: 'app-project-book-cover-image',
  templateUrl: './project-book-cover-image.component.html',
  styleUrls: ['./project-book-cover-image.component.css']
})
export class ProjectBookCoverImageComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('draftCoverImage', { static: true }) bookCoverImage;
  @Input() book: Book;
  /**
   * Optional hard-coded pixel size overrides used by callers that
   * render this component in a slot smaller than the default
   * ~190×304 projects-thumbnail size (e.g. the cart's grid and
   * table views). When provided, they are bound to the img's
   * `max-width` / `min-height` / `max-height` via inline style
   * bindings which win over the scoped CSS defaults in
   * `.component.css`. When omitted (the ProjectsComponent case)
   * the original scoped 190×304 constraints apply and nothing
   * about the projects display changes.
   */
  @Input() width: number;
  @Input() height: number;

  displayScale = 1.0;

  Math = Math;

  private onDestroySubject: Subject<boolean> = new Subject<boolean>();
  private stageWatch = new StageWatch();

  constructor(private bookService: BookService,
              private coverService: CoverService,
              private bodyService: BodyService,
              private resourceService: ResourceService) {
  }

  ngOnInit() {
    this.refreshImagePosition();

    this.resourceService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(resource => {
        if (resource && resource.getId && resource.getId() === this.coverResourceId()) {
          this.onStageMayHaveChanged();
        }
      });

    this.coverService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(cover => {
        if (cover && cover.getId && this.book && this.book.cover
          && cover.getId() === this.book.cover.ID) {
          this.onStageMayHaveChanged();
        }
      });

    // SADDLE_STITCH takes its cover from the Body PDF.
    this.bodyService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(body => {
        if (body && body.getId && this.book && this.book.body
          && body.getId() === this.book.body.ID) {
          this.onStageMayHaveChanged();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.refreshImagePosition();
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  /** Whichever side supplies the cover artwork for this binding. */
  private coverSide(): any {
    if (!this.book) { return null; }
    return this.book.bindingType === 'SADDLE_STITCH' ? this.book.body : this.book.cover;
  }

  private coverResourceId(): number {
    const side = this.coverSide();
    return (side && side.resource_ID) || 0;
  }

  /**
   * Is the standardized cover artwork available?
   *
   * This is the whole fallback decision, isolated so it reads as one thing.
   */
  private hasStandardizedCover(): boolean {
    const side = this.coverSide();
    return !!(side && side.standardized === 'COMPLETE');
  }

  /** `standardizes: true` — always a Book, even before its Cover row loads. */
  private artifactStatus(): PdfArtifactStatus {
    const side = this.coverSide();
    const resourceId = this.coverResourceId();
    const resource = resourceId ? this.resourceService.getLocalItem(resourceId) : null;
    return {
      resourceId: resourceId,
      prepared: resource ? resource.prepared : undefined,
      standardized: side ? side.standardized : undefined,
      standardizes: true
    };
  }

  private onStageMayHaveChanged(): void {
    if (!this.stageWatch.changed(this.artifactStatus())) {
      return;
    }
    this.refreshImagePosition();
  }

  refreshImagePositionDetails(cachedImage: any) {
    try {
      this.bookCoverImage.nativeElement.style.display = "block";
      this.bookCoverImage.nativeElement.src = cachedImage.src;
      // Also plate directly, for the case where the src did not actually
      // change: assigning an identical src fires no `load`, so the handler
      // alone would leave the previous plate in place. A no-op until the
      // element has both loaded and been laid out.
      this.onCoverImageLoaded();
    } catch (error) {
      console.error('Error refreshing image position details:', error);
      return;
    }
  }

  /**
   * Put paper under the artwork.
   *
   * Bound to the img's `load` event because the plate needs two facts that
   * do not exist before it fires: the render's natural size, and the
   * laid-out size of the box it has to sit inside.
   */
  onCoverImageLoaded(): void {
    if ( !this.bookCoverImage ) {
      return;
    }
    paintCoverWhitePlate(this.bookCoverImage.nativeElement);
  }

  refreshImagePosition() {
    if ( !this.book || !this.book.cover ) {
      return;
    }
    const resourceId = this.coverResourceId();

    if ( !resourceId ) {
      this.bookCoverImage.nativeElement.src = '';
      return;
    }

    const standardized = this.hasStandardizedCover();

    requestStagedImage({
      // Two ids, because these are two genuinely different renders and one
      // cache entry must never stand in for the other.
      //
      // `resource-<id>` is deliberately the SAME namespace the Draft cover
      // thumbnail uses, because in the fallback case this is byte-for-byte the
      // same request - so a Project showing both a Draft and its Book fetches
      // the image once and both surfaces share it.
      baseId: standardized ? ('cropped-cover-' + resourceId) : ('resource-' + resourceId),
      stage: bestAvailableStage(this.artifactStatus()),
      fetchUrl: () => this.coverFetch(resourceId, standardized),
      onReady: cachedImage => this.refreshImagePositionDetails(cachedImage),
      onUnavailable: () => {
        this.resourceService.ensureProcessedResource(resourceId);
      }
    });
  }

  /**
   * The request itself, and the reason this component has two of them.
   *
   * ── The rule ─────────────────────────────────────────────────────────
   * The Book **editor** shows the standardized render or nothing: it is where
   * an operator signs off that a book is fit to print, and a near-miss that
   * looks right is worse there than an empty panel.
   *
   * A **thumbnail** is not that. Its job is "which book is this?", and a
   * library grid full of blank tiles because standardization is queued is a
   * worse answer to that question than the cover the customer uploaded. So
   * the thumbnail falls back — confirmed by the product owner 2026-09-15.
   *
   * ── "The same image a Draft would show" is meant literally ───────────
   * The fallback is not merely *similar* to the Draft's: it is the identical
   * call. Same endpoint, same colour, same 72 dpi, same page, same trim
   * geometry, same shifts, same alignment — see
   * `ProjectDraftCoverImageComponent.refreshImagePosition`. The two are meant
   * to be indistinguishable, so if one is ever changed the other must change
   * with it. That is also what makes sharing the `resource-<id>` cache id
   * safe rather than a collision.
   *
   * Note the geometry comes from `body` while the shifts come from `cover`,
   * which mirrors the Draft exactly (`draftBody` / `draftCover`): a cover is
   * laid out against the trim size of the interior it wraps.
   */
  private coverFetch(resourceId: number, standardized: boolean): Observable<string> {
    if (standardized) {
      return this.bookService.getCroppedImageURL(this.book.ID, 'COVER', 144, 1);
    }
    const body = this.book.body;
    const cover = this.book.cover;
    if (!body || !cover) {
      // No geometry to crop against. Fall back to the plain page render
      // rather than sending zeroes, which would request a 0x0 crop.
      return this.resourceService.getImageURL(resourceId, 'COLOR', 72, 1);
    }
    return this.resourceService.getCroppedImageURL(
      resourceId, 'COLOR', 72, 1,
      body.width, body.height,
      cover.horizontalShift, cover.verticalShift,
      body.width + body.spine, 0, 0, 0, 'CENTER_CENTER');
  }
}

